import { CommonModule } from '@angular/common';
import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxCaptchaModule } from 'ngx-captcha';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AlertService, AlertType } from 'src/app/core/services/alert.service';
import { AuthService } from 'src/app/core/services/auth.service';
import { CommonService } from 'src/app/core/services/common.service';
import { CPFService } from 'src/app/core/services/cpf.service';
import { MemberService } from 'src/app/core/services/member.service';
import { ModalService } from 'src/app/core/services/modal.service';
import { PrivacyPolicyService } from 'src/app/core/services/privacy-policy.service';
import { TermsService } from 'src/app/core/services/terms.service';
import { AppButtonComponent } from 'src/app/shared/components/app-button/app-button.component';
import { AppCheckboxComponent } from 'src/app/shared/components/app-checkbox/app-checkbox.component';
import { AppInputCustomComponent, InputMaskTypes } from 'src/app/shared/components/app-input-custom/app-input-custom.component';
import { AppInputComponent, InputType } from 'src/app/shared/components/app-input/app-input.component';
import { AppModalComponent } from 'src/app/shared/components/app-modal/app-modal.component';
import { AppSelectInputComponent, SelectModel } from 'src/app/shared/components/app-select-input/app-select-input.component';
import { PartnerData } from 'src/app/shared/models/partnerData';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-cadastro-socio',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgxCaptchaModule,
    AppInputComponent,
    AppInputCustomComponent,
    AppSelectInputComponent,
    AppButtonComponent,
    AppCheckboxComponent,
    AppModalComponent
  ],
  templateUrl: './cadastro-socio.component.html',
  styleUrl: './cadastro-socio.component.scss',
})
export class CadastroSocioComponent implements OnInit, OnDestroy {

  InputTypeEnum = InputType;
  InputMaskTypesEnum = InputMaskTypes;
  isMinorAge = false;
  modalTitle: string = '';
  modalBodyMessage: string = '';
  modalId: string = 'cpfValidationModal';
  siteKey = environment.siteKey;

  isForeigner: boolean = false;

  // TODO: Rever estrutura de color
  labelColor = localStorage.getItem('primaryColor') || '#fff';

  logoUrl: string = localStorage.getItem('imageLogo') || '';
  isLogged = localStorage.getItem('currentUser') ? true : false;

  private _destroy$ = new Subject<void>();

  memberRegistrationGroup = new FormGroup({
    loginControl: new FormControl<string>('', Validators.required),
    birthDate: new FormControl<string>('', [Validators.required, Validators.maxLength(10)]),
    fullnameControl: new FormControl<string>({ value: '', disabled: true }, [Validators.required, this.fullNameValidator]),
    genderControl: new FormControl<string>({ value: '', disabled: true }, Validators.required),
    emailControl: new FormControl<string>('', [Validators.required, Validators.email]),
    phoneControl: new FormControl<string>('', Validators.required),
    passwordControl: new FormControl<string>('', Validators.required),
    confirmPasswordControl: new FormControl<string>('', [Validators.required, this.passwordMatchValidator.bind(this)]),
    termsConfirmationInputId: new FormControl<boolean>(false, Validators.requiredTrue),
    nameResponsibleControl: new FormControl<string>(''),
    cpfResponsibleControl: new FormControl<string>(''),
    phoneResponsibleControl: new FormControl<string>(''),
    recaptcha: new FormControl<string>('', Validators.required),
    isForeigner: new FormControl<boolean>(false),
    countryControl: new FormControl<any>(1),
  });

  checkboxLabel = ``;

  backOptions = {
    buttonText: 'Voltar',
    buttonSize: 'btn btn-md px-3 btn-secondary',
    borderRadius: '25px'
  };

  buttonOptions = {
    buttonText: 'Cadastrar',
    borderRadius: '25px',
    buttonSize: 'btn btn-sm px-3 btn-primary',
    btnDisabledBackground: '#f2e2b2',
    btnDisabledTextColor: '#000000'
  };

  protected genderOptions = [
    { code: 'M', name: 'Masculino' },
    { code: 'F', name: 'Feminino' },
    { code: 'NI', name: 'Não Informar' }
  ] as SelectModel[];


  protected countriesList = [] as SelectModel[];

  pageTitle = 'Informe os dados para iniciar seu cadastro';
  customErrorMessages: Record<string, string> = {};
  termsAndPoliciesModalId = 'termsAndPoliciesModal';
  termsAndPoliciesModalBodyMessage = '';
  termsAndPoliciesModalTitle = '';
  scrollable = true;

  memberId: string | null = null;
  social: string | null = null;
  indicationId: string | null = null;

  constructor(
    private router: Router,
    private memberService: MemberService,
    private authService: AuthService,
    private location: Location,
    private modalService: ModalService,
    private commonService: CommonService,
    private termsService: TermsService,
    private privacyPolicyService: PrivacyPolicyService,
    private alertService: AlertService,
    private route: ActivatedRoute,
    private cpfService: CPFService
  ) {
    this.authService.isLoggedIn.subscribe(status => {
      if (status) {
        this.router.navigate(['/dashboard']);
      }
      window.scrollTo(0, 0);
    });
  }

  ngOnInit(): void {
    this.memberRegistrationGroup.get('passwordControl')?.valueChanges
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this.memberRegistrationGroup.get('confirmPasswordControl')?.updateValueAndValidity();
      });

    this.route.queryParams.subscribe(params => {
      this.memberId = params['memberId'];
      this.social = params['social'];
      this.indicationId = params['indicationId'];
    });

    this.commonService.getCountries().subscribe((res: any) => {
      this.countriesList = res.map((city: any) => {
        return {
          code: city.id,
          name: city.name
        } as SelectModel;
      });
    })

  }

  get controls(): any {
    return this.memberRegistrationGroup.controls;
  }

  private passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
    const parent = control.parent;
    if (!parent) return null;

    const password = parent?.get('passwordControl')?.value;
    const confirmPassword = parent?.get('confirmPasswordControl')?.value;

    if (password !== confirmPassword) {
      return { passwordMismatch: true };
    }

    return null;
  }

  back() {
    this.location.back();
  }

  onSearchCPF() {
    if (!this.isForeigner) {
      const cpf: string = this.controls.loginControl.value;
      const birthDate: string = this.controls.birthDate.value?.replaceAll('/', '');

      if (cpf?.length === 11 && birthDate?.length === 8) {
        this.alertService.showAlert(AlertType.INFO, 'Validando CPF...');
        this.memberRegistrationGroup.disable();

        // Permitir testes com qualquer CPF em localhost ou no ambiente de desenvolvimento
        if (window.location.hostname === 'localhost' || window.location.hostname === 'sociotorcedordev-clube.efuti.com.br') {
          this.alertService.showAlert(AlertType.INFO, 'Ambiente de desenvolvimento: Validação de CPF ignorada.');
          this.isMinorAge = false;
          this.memberRegistrationGroup.enable();
          this.memberRegistrationGroup.get('nameResponsibleControl')?.reset();
          this.memberRegistrationGroup.get('cpfResponsibleControl')?.reset();
          this.memberRegistrationGroup.get('phoneResponsibleControl')?.reset();
          this.memberRegistrationGroup.get('nameResponsibleControl')?.setValidators(null);
          this.memberRegistrationGroup.get('cpfResponsibleControl')?.setValidators(null);
          this.memberRegistrationGroup.get('phoneResponsibleControl')?.removeValidators(Validators.required);
          this.memberRegistrationGroup.updateValueAndValidity();
          return;
        }

        // Verificação do CPF
        this.memberService.validateMemberIngressoSA(cpf).subscribe({
          next: (isValid: boolean) => {
            if (isValid) {
              // Exibir modal informativo
              this.modalTitle = 'Aviso';
              this.modalBodyMessage =
                'Você já comprou ingresso em nossa plataforma e já está cadastrado. Use seu CPF como usuário e senha para efetuar o login.';
              this.modalService.open(this.modalId);
              this.memberRegistrationGroup.enable();
              return;
            }

            // Continuar o fluxo atual se o CPF não for válido para ingresso
            this.alertService.showAlert(AlertType.INFO, 'Aguarde, seus dados estão sendo validados...');
            this.cpfService.searchCPF(cpf, birthDate).subscribe({
              next: (res) => {
                if (res.code === '0' && res.status === 'OK') {
                  this.memberRegistrationGroup.patchValue({
                    fullnameControl: res.nome.toUpperCase(),
                    genderControl: res.genero.sexo,
                  });

                  this.memberRegistrationGroup.enable();

                  if (this.controls.genderControl.value != null && this.controls.genderControl.value !== "") {
                    this.controls.genderControl.disable();
                  }
                  if (this.controls.fullnameControl.value != null && this.controls.fullnameControl.value !== "") {
                    this.controls.fullnameControl.disable();
                  }

                  this.alertService.showAlert(AlertType.SUCCESS, 'Operação realizada com sucesso');
                } else {
                  this.alertService.showAlert(AlertType.WARNING, res.message);

                  if (res.code > '2' && res.code < '9') {
                    this.memberRegistrationGroup.enable();
                  } else if (['1', '2', '9'].includes(res.code)) {
                    this.memberRegistrationGroup.enable();
                    this.controls.fullnameControl.disable();
                    this.controls.genderControl.disable();
                  }
                }

                const date = new Date(this.controls.birthDate.value!.split('/').reverse().join('-'));
                const age = this.diffYears(date, new Date());
                this.isMinorAge = res.idade ? res.idade < 18 : age < 18;

                if (this.isMinorAge) {
                  this.memberRegistrationGroup.get('nameResponsibleControl')?.setValidators([
                    Validators.required,
                    this.fullNameValidator,
                  ]);
                  this.memberRegistrationGroup.get('cpfResponsibleControl')?.setValidators(Validators.required);
                  this.memberRegistrationGroup.get('phoneResponsibleControl')?.addValidators(Validators.required);
                } else {
                  this.memberRegistrationGroup.get('nameResponsibleControl')?.reset();
                  this.memberRegistrationGroup.get('cpfResponsibleControl')?.reset();
                  this.memberRegistrationGroup.get('phoneResponsibleControl')?.reset();

                  this.memberRegistrationGroup.get('nameResponsibleControl')?.setValidators(null);
                  this.memberRegistrationGroup.get('cpfResponsibleControl')?.setValidators(null);
                  this.memberRegistrationGroup.get('phoneResponsibleControl')?.removeValidators(Validators.required);
                }

                this.memberRegistrationGroup.updateValueAndValidity();

              },
              error: (err) => {
                console.error(err);
                this.alertService.showAlert(AlertType.WARNING, "Validação de CPF indisponível. Preencha os campos manualmente.");
                this.memberRegistrationGroup.enable();
              },
            });
          },
          error: (err) => {
            console.error(err);
            this.alertService.showAlert(AlertType.DANGER, 'Erro ao validar o CPF. Preencha os campos manualmente.');
            this.memberRegistrationGroup.enable();
          },
        });
      }
    }
  }

  private diffYears(date1: Date, date2: Date) {
    let years = date2.getFullYear() - date1.getFullYear();

    // Ajusta caso ainda não tenha passado o "aniversário"
    const m1 = date1.getMonth();
    const m2 = date2.getMonth();

    if (m2 < m1 || (m2 === m1 && date2.getDate() < date1.getDate())) {
      years--;
    }

    return years;
  }


  onModalConfirm(event: any): void {
    this.router.navigate(['/login'], { queryParams: { memberClienteIngressoSA: true } });
  }


  onSubmit(): void {
    if (this.memberRegistrationGroup.valid && this.validName()) {
      // Extrair o parâmetro indicatorUser da URL
      const indicatorUser = this.route.snapshot.queryParamMap.get('indicatorUser');
      // Handle form submission
      const partnerData = this.buildPayloadPartnerData(this.memberRegistrationGroup);
      if (indicatorUser) {
        partnerData.referrerMember = {
          id: indicatorUser
        };
      }
      this.postMember(partnerData);
    } else {
      this.memberRegistrationGroup.markAllAsTouched();
      this.alertService.showAlert(AlertType.DANGER, 'Por favor, corrija os erros no formulário.');
    }
  }

  changeForeigner() {
    this.isForeigner = this.memberRegistrationGroup.get('isForeigner')?.value as boolean;

    if (this.isForeigner) {
      this.memberRegistrationGroup.enable();
      this.memberRegistrationGroup.get('countryControl')?.setValidators(Validators.required);
      this.memberRegistrationGroup.updateValueAndValidity();
    } else {
      this.controls.fullnameControl.disable();
      this.controls.genderControl.disable();
      this.memberRegistrationGroup.get('countryControl')?.setValidators(null);
      this.memberRegistrationGroup.updateValueAndValidity();
    }
  }

  validName(): boolean {
    const regexName = /^[\p{Script=Latin}]+(?:[ '\-][\p{Script=Latin}]+)+$/u;
    const fullName = this.memberRegistrationGroup.get('fullnameControl')?.value?.trim();

    if (!fullName) return false;
    if (fullName?.length < 3) return false;
    if (fullName.split(/\s+/).length < 2) return false;

    return regexName.test(fullName);
  }


  postMember(partnerData: any) {
    this.memberService.postMember(partnerData).subscribe(
      (response) => {
        this.memberService.setMemberId(response.id);
        localStorage.setItem('current-user', JSON.stringify(response));
        if (response.id) {
          this.authService.login(partnerData.cpf, partnerData.password, null, true).subscribe({
            next: (res) => {
              if (this.memberId && this.social) {
                let invitePayload: any = {
                  member: this?.memberId,
                  memberGuest: response.id,
                  socialNetwork: this?.social,
                  link: window.location.href.toString()
                }
                this.memberService.createMemberInviteLink(invitePayload).subscribe(res => {

                })
              }
              if (this.indicationId) {
                let invitePayload: any = {
                  id: this?.indicationId,
                  memberGuestId: response.id,
                  emailRegistered: partnerData.email
                }
                this.memberService.createMemberInviteEmail(invitePayload).subscribe(res => {

                })
              }
              this.router.navigate([`/completar-dados`]);
            }
          });
        }
      }
    );
  }

  private buildPayloadPartnerData(formGroup: FormGroup): PartnerData {
    return {
      birthDate: formGroup.get('birthDate')?.value.split('/').reverse().join('-'),
      cpf: formGroup.get('loginControl')?.value.toUpperCase(),
      email: formGroup.get('emailControl')?.value.toLowerCase(), // Converte o email para minúsculas
      gender: formGroup.get('genderControl')?.value,
      memberSituation: { id: PartnerDataEnum.memberSituation },
      name: formGroup.get('fullnameControl')?.value.trim(),
      originMember: { id: PartnerDataEnum.originMember },
      phone: formGroup.get('phoneControl')?.value,
      isForeigner: formGroup.get('isForeigner')?.value,
      documentType: this.isForeigner ? 2 : 1,
      country: this.isForeigner ? { id: formGroup.get('countryControl')?.value } : { id: 1 },
      registrationReason: { id: PartnerDataEnum.registrationReason },
      password: formGroup.get('passwordControl')?.value,
      memberResponsible: this.isMinorAge ? {
        cpfResponsible: this.memberRegistrationGroup.get('cpfResponsibleControl')?.value,
        nameResponsible: this.memberRegistrationGroup.get('nameResponsibleControl')?.value,
        numberResponsible: this.memberRegistrationGroup.get('phoneResponsibleControl')?.value
      } : null,
      recaptcha: formGroup.get('recaptcha')?.value
    };
  }

  ngOnDestroy(): void {
    // Emit a signal to unsubscribe from the observable
    this._destroy$.next();
    this._destroy$.complete();
  }

  showTermsAndPolicies(event: Event, type: string) {
    event.preventDefault();
    this.scrollable = true;
    if (type === 'terms') {
      this.getTerms();
    } else {
      this.getPrivacyPolicy();
    }
    setTimeout(() => {
      this.modalService.open(this.termsAndPoliciesModalId);
    }, 100);

  }

  getTerms() {
    this.termsService.getTerms().subscribe((data) => {
      this.termsAndPoliciesModalTitle = 'Termos de Uso';
      this.termsAndPoliciesModalBodyMessage = data[0].content;
    });
  }

  getPrivacyPolicy() {
    this.privacyPolicyService.getPrivacyPolicy().subscribe((response: any) => {
      this.termsAndPoliciesModalTitle = 'Política de Privacidade';
      this.termsAndPoliciesModalBodyMessage = response.content[0].description;
    });
  }

  private fullNameValidator(control: AbstractControl): ValidationErrors | null {
    const fullName = control.value?.trim();
    if (!fullName) return { fullNameInvalid: true };

    const nameParts = fullName.split(' ');

    if (nameParts.length < 2) {
      return { fullNameInvalid: true };
    }

    const hasNumbers = /\d/.test(fullName);
    if (hasNumbers) {
      return { fullNameInvalid: true };
    }

    return null;
  }

}

enum PartnerDataEnum {
  memberSituation = '248d6551-0816-4c5f-8ec7-e4382546e832',
  originMember = '0aeb171a-1dae-4847-9dcf-23524c32ec49',
  registrationReason = 'fb10cca0-01e5-45f6-b48e-f60ebeb5ac79'
}
